import { useState } from "react";
import { Trophy, ChevronLeft, ChevronRight, ChevronDown, Users, BookOpen, Building2 } from "lucide-react";
import { Avatar, AvatarFallback } from "../ui/avatar";
import { useNavigate } from "react-router-dom";

const universities = ["All", "BSU", "ADA", "BEU", "ASOIU", "UNEC"];
const majors = ["All", "Computer Science", "Mathematics", "Engineering", "Business", "Physics"];
const cities = ["All", "Baku", "Ganja", "Sumgait", "Mingachevir"];

const studentData = Array.from({ length: 100 }, (_, i) => ({
  rank: i + 1,
  name: ["Ali Aliyev", "Leyla M.", "Rashad H.", "Nigar A.", "Tural K.", "Sabina R.", "Kamran H.", "Aysel H.", "Farid G.", "Elvin M.", "Gulnar A.", "Orkhan B.", "Vusal T.", "Narmin S.", "Samir D.", "Lamiya F.", "Javid A.", "Ulviyya N.", "Ilkin R.", "Sevinj K."][i % 20],
  initials: ["AA", "LM", "RH", "NA", "TK", "SR", "KH", "AH", "FG", "EM", "GA", "OB", "VT", "NS", "SD", "LF", "JA", "UN", "IR", "SK"][i % 20],
  points: Math.max(5000 - i * 42 + Math.floor(Math.random() * 20), 100),
  university: ["BSU", "ADA", "BEU", "ASOIU", "UNEC"][i % 5],
  major: ["Computer Science", "Mathematics", "Engineering", "Business", "Physics"][i % 5],
  city: ["Baku", "Ganja", "Sumgait", "Mingachevir"][i % 4],
  delta: i < 10 ? Math.floor(Math.random() * 6) - 2 : 0
}));

const teacherData = Array.from({ length: 50 }, (_, i) => ({
  rank: i + 1,
  name: ["Prof. Mammadov", "Dr. Hasanova", "Prof. Aliyev", "Dr. Rzayeva", "Prof. Huseynov", "Dr. Guliyeva", "Prof. Karimov", "Dr. Abdullayeva", "Prof. Nasirov", "Dr. Ismayilova"][i % 10],
  initials: ["PM", "DH", "PA", "DR", "PH", "DG", "PK", "DA", "PN", "DI"][i % 10],
  points: Math.max(8000 - i * 120 + Math.floor(Math.random() * 50), 200),
  university: ["BSU", "ADA", "BEU", "ASOIU", "UNEC"][i % 5],
  major: ["Computer Science", "Mathematics", "Engineering", "Business", "Physics"][i % 5],
  city: ["Baku", "Ganja", "Sumgait"][i % 3],
  delta: i < 5 ? Math.floor(Math.random() * 4) - 1 : 0
}));

const classData = Array.from({ length: 40 }, (_, i) => ({
  rank: i + 1,
  name: ["CS201 — Data Structures", "MATH301 — Linear Algebra", "CHEM101 — Chemistry", "PHYS202 — Electromagnetism", "CS101 — Intro to Programming", "ECON201 — Microeconomics", "BIO301 — Molecular Biology", "CS301 — Operating Systems"][i % 8],
  initials: ["CS", "MA", "CH", "PH", "IP", "EC", "MB", "OS"][i % 8],
  points: Math.max(12000 - i * 250 + Math.floor(Math.random() * 100), 500),
  university: ["BSU", "ADA", "BEU", "ASOIU", "UNEC"][i % 5],
  major: ["Computer Science", "Mathematics", "Engineering", "Business", "Physics"][i % 5],
  city: ["Baku", "Ganja", "Sumgait", "Mingachevir"][i % 4],
  members: Math.floor(Math.random() * 50) + 15,
  delta: i < 5 ? Math.floor(Math.random() * 4) - 1 : 0
}));

const groupData = Array.from({ length: 30 }, (_, i) => ({
  rank: i + 1,
  name: ["Robotics Club", "Competitive Programming", "AI Research", "Debate Society", "Photography Club", "Music Club", "Chess Club", "Volunteer Network"][i % 8],
  initials: ["RC", "CP", "AI", "DS", "PC", "MC", "CC", "VN"][i % 8],
  points: Math.max(9000 - i * 200 + Math.floor(Math.random() * 80), 300),
  university: ["BSU", "ADA", "BEU", "ASOIU", "UNEC"][i % 5],
  major: ["Computer Science", "Mathematics", "Engineering", "Business", "Physics"][i % 5],
  city: ["Baku", "Ganja", "Sumgait", "Mingachevir"][i % 4],
  members: Math.floor(Math.random() * 80) + 10,
  delta: i < 5 ? Math.floor(Math.random() * 4) - 1 : 0
}));



const tabConfig = [
{ id: "Students", label: "Students", icon: Users },
{ id: "Teachers", label: "Teachers", icon: Users },
{ id: "Classes", label: "Classes", icon: BookOpen },
{ id: "Groups", label: "Groups", icon: Building2 }];


const getRankStyle = (rank) => {
  if (rank === 1) return "bg-[hsl(43,80%,55%)] text-[hsl(43,80%,15%)]"; // gold
  if (rank === 2) return "bg-[hsl(0,0%,75%)] text-[hsl(0,0%,20%)]"; // silver
  if (rank === 3) return "bg-[hsl(25,60%,55%)] text-[hsl(25,60%,15%)]"; // copper
  return "bg-muted text-muted-foreground";
};

const getPodiumBg = (rank) => {
  if (rank === 1) return "border-[hsl(43,80%,55%)]/30 bg-[hsl(43,80%,55%)]/5";
  if (rank === 2) return "border-[hsl(0,0%,75%)]/30 bg-[hsl(0,0%,75%)]/5";
  if (rank === 3) return "border-[hsl(25,60%,55%)]/30 bg-[hsl(25,60%,55%)]/5";
  return "";
};

const CommunityLeaderboard = () => {
  const [tab, setTab] = useState("Students");
  const [selectedUni, setSelectedUni] = useState("All");
  const [selectedMajor, setSelectedMajor] = useState("All");
  const [selectedCity, setSelectedCity] = useState("All");
  const [page, setPage] = useState(0);
  const navigate = useNavigate();
  const perPage = 20;

  const rawData = tab === "Students" ? studentData : tab === "Teachers" ? teacherData : tab === "Classes" ? classData : groupData;

  const data = rawData.filter((item) => {
    const matchUni = selectedUni === "All" || item.university === selectedUni;
    const matchMajor = selectedMajor === "All" || item.major === selectedMajor;
    const matchCity = selectedCity === "All" || item.city === selectedCity;
    return matchUni && matchMajor && matchCity;
  }).map((item, i) => ({ ...item, rank: i + 1 }));

  const totalPages = Math.ceil(data.length / perPage);
  const visible = data.slice(page * perPage, (page + 1) * perPage);
  const isEntityTab = tab === "Classes" || tab === "Groups";

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="stat-card !p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-2xl bg-secondary/10 flex items-center justify-center">
            <Trophy className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <h2 className="text-lg font-display font-bold text-foreground">Leaderboard</h2>
            <p className="text-xs text-muted-foreground">Top performers ranked by points</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-3 flex-wrap">
          {tabConfig.map((t) =>
          <button key={t.id} onClick={() => {setTab(t.id);setPage(0);}}
          className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-semibold transition-colors ${
          tab === t.id ? "bg-secondary text-secondary-foreground" : "bg-muted text-muted-foreground hover:bg-muted/80"}`
          }>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          <div className="relative">
            <select value={selectedCity} onChange={(e) => {setSelectedCity(e.target.value);setPage(0);}}
            className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-muted text-xs font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring">
              {cities.map((c) => <option key={c} value={c}>{c === "All" ? "All Cities" : c}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
          </div>
          <div className="relative">
            <select value={selectedUni} onChange={(e) => {setSelectedUni(e.target.value);setPage(0);}}
            className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-muted text-xs font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring">
              {universities.map((u) => <option key={u} value={u}>{u === "All" ? "All Universities" : u}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
          </div>
          <div className="relative">
            <select value={selectedMajor} onChange={(e) => {setSelectedMajor(e.target.value);setPage(0);}}
            className="appearance-none pl-3 pr-8 py-2 rounded-xl bg-muted text-xs font-medium text-foreground cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring">
              {majors.map((m) => <option key={m} value={m}>{m === "All" ? "All Majors" : m}</option>)}
            </select>
            <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 w-3 h-3 text-muted-foreground pointer-events-none" />
          </div>
        </div>
      </div>

      {/* Top 3 Podium */}
      {page === 0 && data.length >= 3 &&
      <div className="grid grid-cols-3 gap-3">
          {[1, 0, 2].map((orderIdx, i) => {
          const person = data[orderIdx];
          const heights = ["h-28", "h-32", "h-24"];
          return (
            <button key={i} onClick={() => !isEntityTab && navigate(`/profile/${person.rank}`)}
            className={`stat-card !p-4 flex flex-col items-center justify-end ${heights[i]} hover:scale-[1.02] transition-transform border ${getPodiumBg(orderIdx + 1)}`}>
                <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold mb-2 ${getRankStyle(orderIdx + 1)}`}>
                  {orderIdx + 1}
                </div>
                <Avatar className="w-10 h-10 mb-1">
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-bold">{person.initials}</AvatarFallback>
                </Avatar>
                <p className="text-xs font-semibold text-foreground truncate w-full text-center">{person.name}</p>
                <p className="text-[10px] text-muted-foreground">{person.points.toLocaleString()} pts</p>
                {isEntityTab && "members" in person &&
              <p className="text-[9px] text-muted-foreground">{person.members} members</p>
              }
              </button>);

        })}
        </div>
      }

      {/* List */}
      <div className="stat-card !p-2">
        {data.length === 0 ?
        <div className="p-8 text-center">
            <Trophy className="w-10 h-10 text-muted-foreground/20 mx-auto mb-3" />
            <p className="text-sm font-medium text-muted-foreground">No results for current filters</p>
          </div> :

        <div className="divide-y divide-border">
            {visible.map((u) =>
          <button key={u.rank} onClick={() => !isEntityTab && navigate(`/profile/${u.rank}`)}
          className={`w-full flex items-center gap-3 px-3 py-2.5 hover:bg-muted/50 transition-colors text-left ${u.rank <= 3 ? getPodiumBg(u.rank) : ""}`}>
                <span className={`w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 ${getRankStyle(u.rank)}`}>
                  {u.rank}
                </span>
                <Avatar className="w-8 h-8">
                  <AvatarFallback className="bg-primary/10 text-primary text-[10px] font-bold">{u.initials}</AvatarFallback>
                </Avatar>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{u.name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {u.university} · {u.major}
                    {isEntityTab && "members" in u ? ` · ${u.members} members` : ""}
                  </p>
                </div>
                <span className="text-xs font-semibold text-foreground">{u.points.toLocaleString()} pts</span>
                {u.delta !== 0 &&
            <span className={`text-[10px] font-bold ${u.delta > 0 ? "text-success" : "text-destructive"}`}>
                    {u.delta > 0 ? "▲" : "▼"}{Math.abs(u.delta)}
                  </span>
            }
              </button>
          )}
          </div>
        }
      </div>

      {/* Pagination */}
      {totalPages > 1 &&
      <div className="flex items-center justify-center gap-2">
          <button onClick={() => setPage(Math.max(0, page - 1))} disabled={page === 0}
        className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center disabled:opacity-30 hover:bg-muted/80 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-muted-foreground font-medium">Page {page + 1} of {totalPages}</span>
          <button onClick={() => setPage(Math.min(totalPages - 1, page + 1))} disabled={page >= totalPages - 1}
        className="w-8 h-8 rounded-lg bg-muted flex items-center justify-center disabled:opacity-30 hover:bg-muted/80 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      }
    </div>);

};

export default CommunityLeaderboard;